import React, { FC, useCallback, useContext } from "react";
import {
  Grid,
  Container,
  Backdrop,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  InitialiseParams,
  OnboardingContext,
  OnboardingProvider,
  Step,
} from "../../../store/OnboardingContext";
import Stepper from "@mui/material/Stepper";
import StepperStep from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import {
  backdropSx,
  paperSx,
  redeemGiftcardContainerSx,
  SignUpBackground,
  stepperLabelSx,
  stepperSx,
} from "./SignUpForm.styles";
import SignUpStepBlank from "./Steps/SignUpStepBlank";
import SignUpStepDetails from "./Steps/SignUpStepDetails";
import SignUpStepPhone from "./Steps/SignUpStepPhone";
import SignUpStepPurchase from "./Steps/SignUpStepPurchase";
import SignUpStepSuccess from "./Steps/SignUpStepSuccess";
import SignUpStepGiftcard from "./Steps/SignUpStepGiftcard";
import SignUpStepGetApp from "./Steps/SignUpStepGetApp";

/**
 * PM5 T2 (money path, batch "onboarding") — converted from MUI v4 to MUI v5+
 * (`@mui/material`). See docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS ESCALATION CLASS.** The signup funnel shell. Conversion is
 * styling-only — the step switch, the provider wiring and every prop passed to
 * it are byte-identical to v4.
 *
 * DEAD RULE DROPPED: the `<Typography>` title carried `classes.formTitle`,
 * which was `{}` in the v4 sheet — a class with no declarations. It rendered
 * nothing, so it is not carried across (guide §1.2 row 1).
 */

const SignUpFormInner = ({
  title,
  background,
  route,
}: {
  title?: string;
  background?: boolean;
  route?: "try" | "buy" | "trybuy" | "giftcard" | undefined;
}) => {
  const { stepLabels, activeStep, loading } = useContext(OnboardingContext);

  const PurchaseStep = useCallback(
    () => <SignUpStepPurchase route={route} />,
    [route],
  );

  const ActiveStepComponent = ((): FC => {
    switch (activeStep) {
      case Step.giftcard:
        return SignUpStepGiftcard;
      case Step["phone-input-verify"]:
        return SignUpStepPhone;
      case Step["input-details"]:
        return SignUpStepDetails;
      case Step.purchase:
        return PurchaseStep;
      case Step.success:
        return SignUpStepSuccess;
      case Step["get-app"]:
        return SignUpStepGetApp;
      default:
        return SignUpStepBlank;
    }
  })();

  return (
    <SignUpBackground withImage={background}>
      <Backdrop sx={backdropSx} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Container maxWidth="sm" sx={redeemGiftcardContainerSx}>
        {/* v4's `justify` / `alignContent` / `alignItems` props are gone from
            v9's Grid and are carried by `paperSx` instead — this element is a
            real flex container, so all three did render (guide §2). */}
        <Grid container sx={paperSx}>
          <Grid size={{ xs: 12 }}>
            <Typography align="center" variant="h1">
              {title}
            </Typography>
            <Stepper
              activeStep={stepLabels.findIndex(({ steps }) =>
                steps.includes(activeStep),
              )}
              alternativeLabel
              sx={stepperSx}
            >
              {stepLabels.map(({ steps, label }) => (
                <StepperStep key={steps.join("-")}>
                  <StepLabel sx={stepperLabelSx}>{label}</StepLabel>
                </StepperStep>
              ))}
            </Stepper>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <ActiveStepComponent />
          </Grid>
        </Grid>
      </Container>
    </SignUpBackground>
  );
};

const SignUpForm = (
  props: InitialiseParams & {
    title?: string;
    background?: boolean;
  },
) => (
  <OnboardingProvider {...props}>
    <SignUpFormInner
      title={props.title}
      background={props.background}
      route={props.route}
    />
  </OnboardingProvider>
);

export default SignUpForm;
